/**
 * Serverfuncties van het zakelijke partnerportaal (reisbureaus, hotels en
 * andere zakelijke partners). Commissies, prijzen en betaalstatussen worden
 * altijd server-side bepaald; de browser levert nooit bedragen aan.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

import { bookingInput } from "./booking-input";
import { PAYMENT_LINK_HOURS } from "./partners-shared";

const PARTNER_SELF_COLS =
  "id, company_name, partner_type, status, contact_first_name, contact_last_name, email, phone, kvk_number, vat_number, address, postcode, city, country, iban, account_holder, referral_code, referral_code_active, commission_type, commission_amount, commission_on_own_bookings, commission_on_referral_bookings, allow_self_payment, allow_payment_link, created_at";

type Ctx = {
  supabase: { rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown }> };
  userId: string;
};

async function isAdmin(context: Ctx) {
  const { data } = await context.supabase.rpc("has_role", {
    _user_id: context.userId,
    _role: "admin",
  });
  return data === true;
}

/**
 * Haalt de partner van de ingelogde gebruiker op. Een beheerder mag met
 * `partnerId` het portaal van een specifieke partner bekijken ("Bekijk als
 * partner"); dat wordt vastgelegd in het auditlogboek.
 */
async function requirePartner(
  context: Ctx,
  partnerId?: string | null,
  opts: { allowPending?: boolean; includeOwnBankDetails?: boolean } = {},
) {
  const { adminDb } = await import("./db.server");
  const db = await adminDb();

  if (partnerId) {
    if (!(await isAdmin(context))) throw new Error("Geen beheerdersrechten");
    const { data } = await db
      .from("partners")
      .select(PARTNER_SELF_COLS)
      .eq("id", partnerId)
      .maybeSingle();
    if (!data) throw new Error("Partner niet gevonden");
    return { db, partner: data, viewingAsAdmin: true as const };
  }

  const { data } = await db
    .from("partners")
    .select(PARTNER_SELF_COLS)
    .eq("user_id", context.userId)
    .maybeSingle();
  if (!data) throw new Error("Geen zakelijk partneraccount gekoppeld aan dit account");
  if (data.status === "blocked") throw new Error("Uw partneraccount is geblokkeerd");
  if (data.status === "rejected") throw new Error("Uw aanvraag is afgewezen");
  if (data.status !== "active") {
    // Niet (meer) goedgekeurde partners: geen toegang tot klant-, rit- of financiële gegevens.
    if (!opts.allowPending) throw new Error("Uw partneraccount is nog niet goedgekeurd");
    // Alleen het eigen profiel mag de eerder ingevulde bankgegevens tonen.
    // Dashboard en andere allowPending-paden houden hun gemaskeerde resultaat.
    if (opts.includeOwnBankDetails) {
      return { db, partner: data, viewingAsAdmin: false as const };
    }
    const masked = { ...data, iban: null, account_holder: null };
    return { db, partner: masked as typeof data, viewingAsAdmin: false as const };
  }
  return { db, partner: data, viewingAsAdmin: false as const };
}

const asPartner = z.object({ partnerId: z.string().uuid().optional().nullable() });

/** Unieke partnercode uit de bedrijfsnaam. */
async function makeReferralCode(
  db: Awaited<ReturnType<typeof import("./db.server").adminDb>>,
  companyName: string,
) {
  const base =
    companyName
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 12) || "PARTNER";
  for (let i = 0; i < 40; i += 1) {
    const candidate = i === 0 ? base : `${base}${i + 1}`;
    const { data } = await db
      .from("partners")
      .select("id")
      .ilike("referral_code", candidate)
      .maybeSingle();
    if (!data) return candidate;
  }
  return `${base}${Date.now().toString().slice(-5)}`;
}

/** Aanmelding van een nieuwe zakelijke partner (status: in afwachting). */
export const partnerSignup = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        companyName: z.string().trim().min(2).max(160),
        partnerType: z.string().trim().min(2).max(40),
        kvkNumber: z.string().trim().min(4).max(20),
        vatNumber: z.string().trim().max(24).optional().nullable(),
        address: z.string().trim().min(3).max(160),
        postcode: z.string().trim().min(4).max(12),
        city: z.string().trim().min(2).max(80),
        country: z.string().trim().min(2).max(40),
        contactFirstName: z.string().trim().min(1).max(80),
        contactLastName: z.string().trim().min(1).max(80),
        contactEmail: z.string().trim().email().max(160),
        phone: z.string().trim().min(6).max(40),
        loginEmail: z.string().trim().email().max(160),
        password: z.string().min(8).max(120),
        iban: z.string().trim().min(8).max(40),
        accountHolder: z.string().trim().min(2).max(120),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { adminDb } = await import("./db.server");
    const db = await adminDb();
    const loginEmail = data.loginEmail.toLowerCase();

    const { data: existing } = await db
      .from("partners")
      .select("id")
      .ilike("email", loginEmail)
      .maybeSingle();
    if (existing) {
      return { ok: false as const, error: "Er is al een aanvraag met dit e-mailadres." };
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const created = await supabaseAdmin.auth.admin.createUser({
      email: loginEmail,
      password: data.password,
      email_confirm: true,
      user_metadata: { full_name: `${data.contactFirstName} ${data.contactLastName}`.trim() },
    });
    let userId = created.data.user?.id ?? null;
    if (!userId) {
      // Bestaat het account al (bijvoorbeeld als klant)? Alleen koppelen als de
      // aanvrager aantoont eigenaar te zijn door het juiste wachtwoord te geven.
      const { createClient } = await import("@supabase/supabase-js");
      const key = process.env["SUPABASE_PUBLISHABLE_KEY"] ?? process.env["SUPABASE_ANON_KEY"] ?? "";
      const verifier = createClient(process.env["SUPABASE_URL"]!, key, {
        auth: { persistSession: false, autoRefreshToken: false, storage: undefined },
        global: {
          fetch: (input, init) => {
            const h = new Headers(init?.headers);
            if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) {
              h.delete("Authorization");
            }
            h.set("apikey", key);
            return fetch(input, { ...init, headers: h });
          },
        },
      });
      const { data: signIn } = await verifier.auth.signInWithPassword({
        email: loginEmail,
        password: data.password,
      });
      userId = signIn.user?.id ?? null;
      if (!userId) {
        return {
          ok: false as const,
          error: "Dit e-mailadres kan niet worden gebruikt. Neem contact met ons op.",
        };
      }
    }

    const referralCode = await makeReferralCode(db, data.companyName);
    const { error } = await db.from("partners").insert({
      user_id: userId,
      company_name: data.companyName,
      partner_type: data.partnerType,
      status: "pending",
      contact_first_name: data.contactFirstName,
      contact_last_name: data.contactLastName,
      email: loginEmail,
      phone: data.phone,
      kvk_number: data.kvkNumber,
      vat_number: data.vatNumber || null,
      address: data.address,
      postcode: data.postcode,
      city: data.city,
      country: data.country,
      iban: data.iban,
      account_holder: data.accountHolder,
      referral_code: referralCode,
    });
    if (error) return { ok: false as const, error: error.message };

    await db.from("user_roles").insert({ user_id: userId, role: "partner" as never });

    // Kwam dit bedrijf uit een B2B-campagne? Dan koppelen wij de lead zodat de
    // campagnebron bewaard blijft.
    {
      const { data: created } = await db
        .from("partners")
        .select("id")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      const { linkLeadToPartner } = await import("./b2b-send.server");
      await linkLeadToPartner(loginEmail, created?.id ?? null);
    }

    const { sendMail } = await import("./mailer.server");
    await sendMail({
      to: loginEmail,
      eventType: "partner_signup_received",
      subject: "Wij hebben uw aanmelding als zakelijke partner ontvangen",
      text: [
        `Beste ${data.contactFirstName},`,
        "",
        `Wij hebben de aanmelding van ${data.companyName} voor het zakelijke partnerprogramma van Taxiritje.nl ontvangen.`,
        "",
        "Wij controleren uw aanvraag handmatig. Zodra uw account is goedgekeurd, ontvangt u van ons bericht en kunt u inloggen op uw partnerportaal.",
        "",
        "Met vriendelijke groet,",
        "Taxiritje.nl",
      ].join("\n"),
    });
    await sendMail({
      to: "info@taxiritje.nl",
      eventType: "partner_signup_admin",
      subject: `Nieuwe partneraanmelding: ${data.companyName}`,
      compact: true,
      text: [
        `Bedrijf: ${data.companyName}`,
        `Type: ${data.partnerType}`,
        `Contact: ${data.contactFirstName} ${data.contactLastName}`,
        `E-mail: ${loginEmail}`,
        `Telefoon: ${data.phone}`,
        `KVK: ${data.kvkNumber}`,
        `Btw: ${data.vatNumber || "-"}`,
        `Adres: ${data.address}, ${data.postcode} ${data.city} (${data.country})`,
        `IBAN: ${data.iban} (${data.accountHolder})`,
        `Partnercode: ${referralCode}`,
      ].join("\n"),
    });

    return { ok: true as const, referralCode };
  });

/** Partnerprofiel van de ingelogde gebruiker (of het bekeken partneraccount). */
export const getMyPartner = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => asPartner.parse(input ?? {}))
  .handler(async ({ data, context }) => {
    try {
      const { partner, viewingAsAdmin } = await requirePartner(
        context as unknown as Ctx,
        data.partnerId ?? null, { allowPending: true, includeOwnBankDetails: true });
      return { partner, viewingAsAdmin };
    } catch {
      return { partner: null, viewingAsAdmin: false };
    }
  });

/** Bedrijfsgegevens bijwerken. */
export const updatePartnerProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    asPartner
      .extend({
        companyName: z.string().trim().min(2).max(160),
        contactFirstName: z.string().trim().min(1).max(80),
        contactLastName: z.string().trim().min(1).max(80),
        phone: z.string().trim().min(6).max(40),
        address: z.string().trim().min(3).max(160),
        postcode: z.string().trim().min(4).max(12),
        city: z.string().trim().min(2).max(80),
        country: z.string().trim().min(2).max(40),
        kvkNumber: z.string().trim().min(4).max(20),
        vatNumber: z.string().trim().max(24).optional().nullable(),
        iban: z.string().trim().min(8).max(40),
        accountHolder: z.string().trim().min(2).max(120),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { db, partner } = await requirePartner(
      context as unknown as Ctx,
      data.partnerId ?? null, { allowPending: true });
    const { error } = await db
      .from("partners")
      .update({
        company_name: data.companyName,
        contact_first_name: data.contactFirstName,
        contact_last_name: data.contactLastName,
        phone: data.phone,
        address: data.address,
        postcode: data.postcode,
        city: data.city,
        country: data.country,
        kvk_number: data.kvkNumber,
        vat_number: data.vatNumber || null,
        iban: data.iban,
        account_holder: data.accountHolder,
      })
      .eq("id", partner.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const BOOKING_COLS =
  "id, booking_number, first_name, last_name, from_label, to_label, pickup_address, dropoff_address, pickup_date, pickup_time, passengers, luggage, hand_luggage, customer_price, status, payment_status, partner_source_type, booked_by_partner, partner_payment_mode, payment_link_sent_at, payment_link_expires_at, confirmation_token, email, phone, flight_number, notes, created_at";

/** Ritten van de partner: eigen boekingen én ritten van gekoppelde klanten. */
export const listPartnerBookings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => asPartner.parse(input ?? {}))
  .handler(async ({ data, context }) => {
    const { db, partner } = await requirePartner(
      context as unknown as Ctx,
      data.partnerId ?? null,
    );
    const { data: rows } = await db
      .from("bookings")
      .select(BOOKING_COLS)
      .eq("partner_id", partner.id)
      .order("created_at", { ascending: false })
      .limit(500);
    return (rows ?? []).map((b) => ({
      ...b,
      customer_price: Number(b.customer_price),
      passenger: `${b.first_name} ${b.last_name}`.trim(),
    }));
  });

/** Dashboardcijfers van de huidige maand. */
export const getPartnerDashboard = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => asPartner.parse(input ?? {}))
  .handler(async ({ data, context }) => {
    const { db, partner } = await requirePartner(
      context as unknown as Ctx,
      data.partnerId ?? null, { allowPending: true });
    const monthStart = new Date();
    monthStart.setUTCDate(1);
    monthStart.setUTCHours(0, 0, 0, 0);
    const from = monthStart.toISOString();

    const [{ data: bookings }, { data: commissions }, { data: customers }, { data: payouts }] =
      await Promise.all([
        db
          .from("bookings")
          .select(
            "id, booking_number, first_name, last_name, from_label, to_label, pickup_date, pickup_time, customer_price, status, payment_status, partner_source_type, booked_by_partner, created_at, payment_link_expires_at, confirmation_token",
          )
          .eq("partner_id", partner.id)
          .order("created_at", { ascending: false })
          .limit(500),
        db
          .from("partner_commissions")
          .select("commission_amount, commission_status, created_at")
          .eq("partner_id", partner.id)
          .limit(2000),
        db
          .from("partner_customers")
          .select("id, linked_at")
          .eq("partner_id", partner.id)
          .limit(2000),
        db
          .from("partner_payouts")
          .select("amount, status")
          .eq("partner_id", partner.id)
          .limit(500),
      ]);

    const all = bookings ?? [];
    const thisMonth = all.filter((b) => (b.created_at ?? "") >= from);
    const sum = (rows: { commission_amount: number | string }[]) =>
      Math.round(rows.reduce((t, r) => t + Number(r.commission_amount), 0) * 100) / 100;
    const comm = commissions ?? [];

    return {
      ownBookingsMonth: thisMonth.filter((b) => b.booked_by_partner).length,
      referralBookingsMonth: thisMonth.filter(
        (b) => !b.booked_by_partner && b.partner_source_type === "referral",
      ).length,
      newCustomersMonth: (customers ?? []).filter((c) => (c.linked_at ?? "") >= from).length,
      customersTotal: (customers ?? []).length,
      commissionPending: sum(comm.filter((c) => c.commission_status === "pending")),
      commissionAvailable: sum(comm.filter((c) => c.commission_status === "approved")),
      commissionPaid: sum(comm.filter((c) => c.commission_status === "paid")),
      commissionTotal: sum(comm.filter((c) => c.commission_status !== "cancelled")),
      payoutsPaid:
        Math.round(
          (payouts ?? [])
            .filter((p) => p.status === "paid")
            .reduce((t, p) => t + Number(p.amount), 0) * 100,
        ) / 100,
      waitingForPayment: all.filter(
        (b) => b.payment_status !== "paid" && b.status === "payment_pending",
      ).length,
      recent: all.slice(0, 10).map((b) => ({
        ...b,
        customer_price: Number(b.customer_price),
        passenger: `${b.first_name} ${b.last_name}`.trim(),
      })),
    };
  });

/** Klanten die via de partnercode gekoppeld zijn. */
export const listPartnerCustomers = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => asPartner.parse(input ?? {}))
  .handler(async ({ data, context }) => {
    const { db, partner } = await requirePartner(
      context as unknown as Ctx,
      data.partnerId ?? null,
    );
    const [{ data: rows }, { data: bookings }, { data: commissions }] = await Promise.all([
      db
        .from("partner_customers")
        .select("id, customer_email, customer_name, linked_via_code, linked_at, status")
        .eq("partner_id", partner.id)
        .order("linked_at", { ascending: false })
        .limit(1000),
      db.from("bookings").select("email").eq("partner_id", partner.id).limit(2000),
      db
        .from("partner_commissions")
        .select("commission_amount, commission_status, booking_id")
        .eq("partner_id", partner.id)
        .limit(2000),
    ]);

    const rideCount = new Map<string, number>();
    for (const b of bookings ?? []) {
      const key = (b.email ?? "").toLowerCase();
      rideCount.set(key, (rideCount.get(key) ?? 0) + 1);
    }
    const commissionTotal =
      Math.round(
        (commissions ?? [])
          .filter((c) => c.commission_status !== "cancelled")
          .reduce((t, c) => t + Number(c.commission_amount), 0) * 100,
      ) / 100;

    return {
      commissionTotal,
      customers: (rows ?? []).map((c) => ({
        id: c.id,
        // Privacy: alleen naam en gemaskeerd e-mailadres.
        name: c.customer_name || c.customer_email.split("@")[0],
        emailMasked: c.customer_email.replace(/^(.).*?(@.*)$/, "$1•••$2"),
        linked_via_code: c.linked_via_code,
        linked_at: c.linked_at,
        status: c.status,
        rides: rideCount.get(c.customer_email.toLowerCase()) ?? 0,
      })),
    };
  });

/** Commissieoverzicht per rit. */
export const listPartnerCommissions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => asPartner.parse(input ?? {}))
  .handler(async ({ data, context }) => {
    const { db, partner } = await requirePartner(
      context as unknown as Ctx,
      data.partnerId ?? null,
    );
    const { data: rows } = await db
      .from("partner_commissions")
      .select(
        "id, booking_id, commission_amount, commission_status, source_type, created_at, approved_at",
      )
      .eq("partner_id", partner.id)
      .order("created_at", { ascending: false })
      .limit(1000);
    const ids = (rows ?? []).map((r) => r.booking_id);
    const { data: bookings } = ids.length
      ? await db
          .from("bookings")
          .select(
            "id, booking_number, first_name, last_name, pickup_date, from_label, to_label, status",
          )
          .in("id", ids)
      : { data: [] };
    const byId = new Map((bookings ?? []).map((b) => [b.id, b]));
    return (rows ?? []).map((r) => {
      const b = byId.get(r.booking_id);
      return {
        id: r.id,
        amount: Number(r.commission_amount),
        status: r.commission_status,
        sourceType: r.source_type,
        createdAt: r.created_at,
        bookingNumber: b?.booking_number ?? "-",
        passenger: b ? `${b.first_name} ${b.last_name}`.trim() : "-",
        route: b ? `${b.from_label} → ${b.to_label}` : "-",
        pickupDate: b?.pickup_date ?? null,
        rideStatus: b?.status ?? "-",
      };
    });
  });

/** Uitbetalingen van commissie. */
export const listPartnerPayouts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => asPartner.parse(input ?? {}))
  .handler(async ({ data, context }) => {
    const { db, partner } = await requirePartner(
      context as unknown as Ctx,
      data.partnerId ?? null,
    );
    const { data: rows } = await db
      .from("partner_payouts")
      .select("id, reference, period_start, period_end, amount, status, paid_at, note, created_at")
      .eq("partner_id", partner.id)
      .order("created_at", { ascending: false })
      .limit(200);
    return (rows ?? []).map((p) => ({ ...p, amount: Number(p.amount) }));
  });

/** Rit boeken door de partner: zelf betalen of betaallink naar de klant. */
export const createPartnerBooking = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    bookingInput
      .extend({
        partnerId: z.string().uuid().optional().nullable(),
        paymentMode: z.enum(["partner_direct", "customer_link"]),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { db, partner } = await requirePartner(
      context as unknown as Ctx,
      data.partnerId ?? null,
    );
    if (partner.status !== "active") {
      throw new Error("Uw partneraccount is nog niet goedgekeurd");
    }
    if (data.paymentMode === "partner_direct" && !partner.allow_self_payment) {
      throw new Error("Zelf betalen is voor uw account niet beschikbaar");
    }
    if (data.paymentMode === "customer_link" && !partner.allow_payment_link) {
      throw new Error("Een betaallink versturen is voor uw account niet beschikbaar");
    }

    const { partnerId: _ignored, paymentMode, ...bookingData } = data;
    const { createBookingCore } = await import("./booking-create.server");
    const result = await createBookingCore(bookingData, {
      partnerId: partner.id,
      bookedByPartner: true,
      paymentMode,
    });

    if (paymentMode === "customer_link") {
      await sendPaymentLinkMail(db, result.bookingNumber, partner.company_name);
    }
    return { ...result, paymentMode };
  });

/** Interne hulpfunctie: betaallink (opnieuw) versturen naar de passagier. */
async function sendPaymentLinkMail(
  db: Awaited<ReturnType<typeof import("./db.server").adminDb>>,
  bookingNumber: string,
  partnerName: string,
) {
  const { data: booking } = await db
    .from("bookings")
    .select(
      "id, booking_number, first_name, email, from_label, to_label, pickup_date, pickup_time, passengers, luggage, hand_luggage, customer_price, payment_status, confirmation_token",
    )
    .eq("booking_number", bookingNumber)
    .maybeSingle();
  if (!booking) throw new Error("Rit niet gevonden");
  if (booking.payment_status === "paid") throw new Error("Deze rit is al betaald");

  const expires = new Date(Date.now() + PAYMENT_LINK_HOURS * 3600_000).toISOString();
  await db
    .from("bookings")
    .update({
      payment_link_sent_at: new Date().toISOString(),
      payment_link_expires_at: expires,
      payment_link_reminded_at: null,
      partner_payment_mode: "customer_link",
    })
    .eq("id", booking.id);

  const { SITE_URL } = await import("./site");
  const { dateNL, timeNL, euro } = await import("./pricing");
  const link = `${SITE_URL}/betalen/${booking.booking_number}?token=${booking.confirmation_token}`;
  const { sendMail } = await import("./mailer.server");
  await sendMail({
    to: booking.email,
    bookingId: booking.id,
    eventType: "partner_payment_link",
    subject: `Rond uw taxirit af — ${booking.booking_number}`,
    text: [
      `Beste ${booking.first_name},`,
      "",
      `Er is een taxi voor u gereserveerd via ${partnerName}.`,
      "",
      "**Uw rit**",
      `Van: ${booking.from_label}`,
      `Naar: ${booking.to_label}`,
      `Datum: ${dateNL(booking.pickup_date)} om ${timeNL(booking.pickup_time)}`,
      `Personen: ${booking.passengers}`,
      `Bagage: ${booking.luggage} grote koffers, ${booking.hand_luggage} kleine bagage`,
      `Totaalbedrag: ${euro(Number(booking.customer_price))}`,
      "",
      "Om uw rit definitief te bevestigen, vragen wij u de betaling af te ronden:",
      link,
      "",
      `Deze betaallink is ${PAYMENT_LINK_HOURS} uur geldig. Uw rit wordt definitief bevestigd en ingepland nadat de betaling succesvol is afgerond. Daarna ontvangt u automatisch uw bevestiging.`,
      "",
      "Met vriendelijke groet,",
      "Taxiritje.nl",
    ].join("\n"),
  });
  return link;
}

/** Betaallink opnieuw versturen. */
export const resendPartnerPaymentLink = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    asPartner.extend({ bookingId: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { db, partner } = await requirePartner(
      context as unknown as Ctx,
      data.partnerId ?? null,
    );
    if (!partner.allow_payment_link) throw new Error("Betaallinks zijn voor uw account uitgeschakeld");
    const { data: booking } = await db
      .from("bookings")
      .select("booking_number, partner_id")
      .eq("id", data.bookingId)
      .maybeSingle();
    if (!booking || booking.partner_id !== partner.id) throw new Error("Rit niet gevonden");
    const link = await sendPaymentLinkMail(db, booking.booking_number, partner.company_name);
    return { ok: true, link };
  });

/** Betaallink kopiëren: geeft de beveiligde betaallink terug. */
export const getPartnerPaymentLink = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    asPartner.extend({ bookingId: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { db, partner } = await requirePartner(
      context as unknown as Ctx,
      data.partnerId ?? null,
    );
    const { data: booking } = await db
      .from("bookings")
      .select("booking_number, confirmation_token, partner_id")
      .eq("id", data.bookingId)
      .maybeSingle();
    if (!booking || booking.partner_id !== partner.id) throw new Error("Rit niet gevonden");
    const { SITE_URL } = await import("./site");
    return {
      link: `${SITE_URL}/betalen/${booking.booking_number}?token=${booking.confirmation_token}`,
    };
  });

/** Onbetaalde aanvraag annuleren. */
export const cancelPartnerPaymentRequest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    asPartner.extend({ bookingId: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { db, partner } = await requirePartner(
      context as unknown as Ctx,
      data.partnerId ?? null,
    );
    const { data: booking } = await db
      .from("bookings")
      .select("id, partner_id, payment_status")
      .eq("id", data.bookingId)
      .maybeSingle();
    if (!booking || booking.partner_id !== partner.id) throw new Error("Rit niet gevonden");
    if (booking.payment_status === "paid") throw new Error("Deze rit is al betaald");
    await db
      .from("bookings")
      .update({ status: "cancelled", payment_status: "cancelled" })
      .eq("id", booking.id);
    await db
      .from("payments")
      .update({ status: "cancelled" })
      .eq("booking_id", booking.id)
      .eq("status", "pending");
    return { ok: true };
  });

/** Publieke controle van een partnercode in de normale boekingsflow. */
export const checkReferralCode = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ code: z.string().trim().min(3).max(32) }).parse(input),
  )
  .handler(async ({ data }) => {
    const { partnerByCode } = await import("./partner-link.server");
    const partner = await partnerByCode(data.code);
    if (!partner) return { valid: false as const };
    return { valid: true as const, companyName: partner.company_name, code: partner.referral_code };
  });
